import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface VerificationHistory {
  code: string;
  value: number;
  usedAt: Date;
}

interface GiftCardVerificationProps {
  cafeId: string;
  cafeOwnerId: string;
}

const GiftCardVerification: React.FC<GiftCardVerificationProps> = ({ cafeId, cafeOwnerId }) => {
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; value?: number } | null>(null);
  const [history, setHistory] = useState<VerificationHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cafeId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cafeOwnerToken');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/gift-cards/verification-history/${cafeId}`,
        getAuthHeaders()
      );

      if (response.data.success) {
        setHistory(response.data.data);
      }
    } catch (err) {
      console.error('History load error:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleVerify = async () => {
    if (!code || code.length !== 9) { // xxxx-xxxx format
      setResult({ success: false, message: 'Geçersiz kod formatı. Format: xxxx-xxxx' });
      return;
    }

    setVerifying(true);
    setResult(null);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/gift-cards/verify`,
        {
          code,
          cafeId,
          cafeOwnerId,
        },
        getAuthHeaders()
      );

      if (response.data.success) {
        setResult({
          success: true,
          message: `✅ ${response.data.data.value} TL gift card onaylandı!`,
          value: response.data.data.value,
        });
        setCode('');
        loadHistory();
      } else {
        setResult({
          success: false,
          message: response.data.message || '❌ Kod doğrulanamadı',
        });
      }
    } catch (err: any) {
      setResult({
        success: false,
        message: err.response?.data?.message || '❌ Bir hata oluştu',
      });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        🎁 Gift Card Doğrulama
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Müşteriden aldığınız 8 karakterli kodu girin (xxxx-xxxx formatında)
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Gift Card Kodu"
              placeholder="xxxx-xxxx"
              value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase())}
              disabled={verifying}
              inputProps={{
                maxLength: 9,
                style: { fontFamily: 'monospace', fontSize: '1.2rem' },
              }}
            />
            <Button
              variant="contained"
              onClick={handleVerify}
              disabled={verifying || !code}
              sx={{ minWidth: 120 }}
            >
              {verifying ? <CircularProgress size={24} /> : 'Doğrula'}
            </Button>
          </Box>

          {result && (
            <Alert
              severity={result.success ? 'success' : 'error'}
              icon={result.success ? <CheckIcon /> : <CancelIcon />}
              sx={{ mt: 2 }}
            >
              <Typography variant="h6">{result.message}</Typography>
              {result.value && (
                <Typography variant="body2">
                  Müşteriye {result.value} TL tutarında hizmet verebilirsiniz.
                </Typography>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* History */}
      <Typography variant="h6" gutterBottom>
        Son Kullanılan Kodlar
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kod</TableCell>
              <TableCell>Değer</TableCell>
              <TableCell>Kullanım Tarihi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingHistory ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : history.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Henüz kullanılan kod yok
                </TableCell>
              </TableRow>
            ) : (
              history.map((item, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{item.code}</TableCell>
                  <TableCell>
                    <Chip label={`${item.value} TL`} color="success" size="small" />
                  </TableCell>
                  <TableCell>{new Date(item.usedAt).toLocaleString('tr-TR')}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default GiftCardVerification;
